import {
  Box,
  Dialog,
  DialogContent,
  Typography,
  IconButton,
  Slider,
  Grid,
  CircularProgress,
  Chip,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import LabelIcon from "@mui/icons-material/Label";
import InfoIcon from "@mui/icons-material/Info";
import { useState, useRef, useEffect } from "react";
import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

// 深色毛玻璃效果的对话框 - 企业化设计
const GlassDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    background: "rgba(0, 0, 0, 0.3)",
    backdropFilter: "blur(15px)",
    WebkitBackdropFilter: "blur(15px)",
    borderRadius: "20px",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
    maxWidth: "1000px",
    width: "90%",
    maxHeight: "90vh",
    padding: 0,
    overflow: "hidden",
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(255, 255, 255, 0.05)",
      borderRadius: 20,
      pointerEvents: "none",
    },
  },
}));

// 对话框标题栏
const DialogHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "16px 24px",
  position: "relative",
  zIndex: 1,
}));

// 详情页容器 - 紧凑布局
const DetailContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(3),
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    gap: theme.spacing(2),
  },
}));

// 详情页左侧信息 - 紧凑设计
const DetailInfo = styled(Box)(({ theme }) => ({
  flex: "0 0 280px",
  position: "relative",
  zIndex: 1,
  [theme.breakpoints.down("md")]: {
    flex: "none",
  },
}));

// 详情页右侧内容 - 紧凑设计
const DetailContent = styled(Box)(({ theme }) => ({
  flex: 1,
  position: "relative",
  zIndex: 1,
}));

// 视频区域容器 - 紧凑设计
const VideoSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: 0,
}));

// 视频容器 - 企业化设计
const VideoContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "400px",
  borderRadius: "12px",
  overflow: "hidden",
  marginTop: theme.spacing(1.5),
  background: "rgba(0, 0, 0, 0.3)",
  border: "1px solid rgba(255, 255, 255, 0.15)",
  "& iframe": {
    width: "100%",
    height: "100%",
    border: "none",
  },
}));

// 音频播放器容器 - 企业化设计
const AudioPlayerContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(2.5),
  background: "rgba(0, 0, 0, 0.25)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  borderRadius: "12px",
  border: "1px solid rgba(255, 255, 255, 0.15)",
  marginTop: theme.spacing(1.5),
  marginBottom: 0,
}));

// 音频控制按钮
const AudioControlButton = styled(IconButton)(({ theme }) => ({
  color: "rgba(255, 255, 255, 0.95)",
  fontSize: "4.5rem",
  transition: "all 0.2s ease",
  padding: theme.spacing(1),
  "&:hover": {
    color: "#fff",
    transform: "scale(1.05)",
  },
}));

// 音频进度条
const AudioSlider = styled(Slider)(({ theme }) => ({
  color: "rgba(255, 255, 255, 0.95)",
  height: 6,
  transition: "all 0.2s ease",
  "& .MuiSlider-track": {
    border: "none",
    background: "rgba(255, 255, 255, 0.95)",
    transition: "all 0.2s ease",
  },
  "& .MuiSlider-thumb": {
    width: 18,
    height: 18,
    backgroundColor: "#fff",
    transition: "all 0.2s ease",
    "&:hover, &.Mui-focusVisible": {
      boxShadow: "0 0 0 8px rgba(255, 255, 255, 0.16)",
    },
    "&.Mui-active": {
      width: 20,
      height: 20,
    },
  },
  "& .MuiSlider-rail": {
    opacity: 0.3,
    background: "rgba(255, 255, 255, 0.2)",
    transition: "all 0.2s ease",
  },
  "&:hover": {
    "& .MuiSlider-thumb": {
      width: 20,
      height: 20,
    },
    "& .MuiSlider-track": {
      height: 8,
    },
    "& .MuiSlider-rail": {
      height: 8,
    },
  },
}));

// 获取 YouTube 视频 ID
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  // 处理带有 si 参数的 URL
  const cleanUrl = url.split("?")[0];
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = cleanUrl.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

const VisionSpaceDetail = ({ open, onClose, activity }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchActivityDetails = async () => {
      if (!activity || !activity._id) {
        setDetail(null);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(
          `${API_BASE_URL}/vision-space/${activity._id}`
        );
        console.log("获取到的活动详情:", response.data);
        setDetail(response.data);
      } catch (err) {
        console.error("获取活动详情失败:", err);
        setError("获取活动详情失败，请稍后再试");
      } finally {
        setLoading(false);
      }
    };

    fetchActivityDetails();
  }, [activity]);

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSliderChange = (_, value) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value;
      setCurrentTime(value);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  if (!open || !activity) return null;

  if (loading) {
    return (
      <GlassDialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogContent
          sx={{
            p: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
            position: "relative",
            zIndex: 1,
          }}
        >
          <CircularProgress sx={{ color: "rgba(255, 255, 255, 0.8)" }} />
        </DialogContent>
      </GlassDialog>
    );
  }

  if (!detail) {
    return (
      <GlassDialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogContent sx={{ p: 3, position: "relative", zIndex: 1 }}>
          <Typography
            sx={{
              color: "rgba(255, 255, 255, 0.9)",
              textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
              textAlign: "center",
            }}
          >
            {error || "无法加载活动详情"}
          </Typography>
        </DialogContent>
      </GlassDialog>
    );
  }

  const videoId =
    detail.mediaType === "video" ? getYouTubeVideoId(detail.mediaUrl) : null;
  const audioUrl =
    detail.mediaType === "audio" ? `${API_BASE_URL}${detail.audioUrl}` : null;

  console.log("媒体类型:", detail.mediaType);
  console.log("视频URL:", detail.mediaUrl);
  console.log("视频ID:", videoId);
  console.log("音频URL:", audioUrl);

  return (
    <GlassDialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
    >
      {/* 标题栏 */}
      <DialogHeader>
        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 600,
            color: "rgba(255, 255, 255, 0.95)",
            textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
          }}
        >
          活动详情
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: "rgba(255, 255, 255, 0.7)",
            "&:hover": {
              color: "rgba(255, 255, 255, 0.9)",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogHeader>

      <DialogContent sx={{ p: 3, position: "relative", zIndex: 1 }}>
        <DetailContainer>
          {/* 左侧信息 - 紧凑企业化设计 */}
          <DetailInfo>
            <Typography
              variant="h5"
              sx={{
                mb: 1.5,
                fontWeight: 600,
                color: "rgba(255, 255, 255, 0.95)",
                fontSize: { xs: "1.25rem", md: "1.5rem" },
                lineHeight: 1.3,
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
              }}
            >
              {detail.title}
            </Typography>

            <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
              <LabelIcon
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  fontSize: "1rem",
                }}
              />
              <Chip
                label={detail.type}
                size="small"
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "rgba(255, 255, 255, 0.9)",
                  fontWeight: 500,
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  fontSize: "0.75rem",
                  height: "24px",
                }}
              />
            </Box>

            <Typography
              variant="body2"
              sx={{
                fontSize: "0.9rem",
                lineHeight: 1.6,
                color: "rgba(255, 255, 255, 0.8)",
                mb: 2.5,
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
              }}
            >
              {detail.description}
            </Typography>

            <Divider
              sx={{
                borderColor: "rgba(255, 255, 255, 0.1)",
                mb: 2,
              }}
            />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AccessTimeIcon
                  sx={{
                    color: "rgba(255, 255, 255, 0.7)",
                    fontSize: "1rem",
                  }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "rgba(255, 255, 255, 0.6)",
                      textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                      mb: 0.25,
                    }}
                  >
                    活动日期
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      color: "rgba(255, 255, 255, 0.9)",
                      fontWeight: 500,
                      textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                    }}
                  >
                    {new Date(detail.date).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <InfoIcon
                  sx={{
                    color: "rgba(255, 255, 255, 0.7)",
                    fontSize: "1rem",
                  }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "rgba(255, 255, 255, 0.6)",
                      textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                      mb: 0.25,
                    }}
                  >
                    状态
                  </Typography>
                  <Chip
                    label={detail.status === "upcoming" ? "即将开始" : "已完成"}
                    size="small"
                    sx={{
                      backgroundColor:
                        detail.status === "upcoming"
                          ? "rgba(33, 150, 243, 0.2)"
                          : "rgba(76, 175, 80, 0.2)",
                      color:
                        detail.status === "upcoming"
                          ? "rgba(144, 202, 249, 0.95)"
                          : "rgba(129, 199, 132, 0.95)",
                      fontWeight: 500,
                      border:
                        detail.status === "upcoming"
                          ? "1px solid rgba(33, 150, 243, 0.3)"
                          : "1px solid rgba(76, 175, 80, 0.3)",
                      fontSize: "0.75rem",
                      height: "24px",
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </DetailInfo>

          {/* 右侧内容 - 紧凑企业化设计 */}
          <DetailContent>
            <Box
              sx={{
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <InfoIcon
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  fontSize: "1rem",
                }}
              />
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  color: "rgba(255, 255, 255, 0.9)",
                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                  letterSpacing: "0.01em",
                }}
              >
                活动详情
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.9rem",
                lineHeight: 1.7,
                color: "rgba(255, 255, 255, 0.85)",
                mb: detail.mediaType !== "none" ? 2 : 0,
                whiteSpace: "pre-wrap",
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
              }}
            >
              {detail.content}
            </Typography>

            {detail.mediaType !== "none" && (
              <VideoSection>
                <Box
                  sx={{
                    mb: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <PlayCircleOutlineIcon
                    sx={{
                      fontSize: "1rem",
                      color: "rgba(255, 255, 255, 0.7)",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      color: "rgba(255, 255, 255, 0.9)",
                      textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                      letterSpacing: "0.01em",
                    }}
                  >
                    {detail.mediaType === "video" ? "活动视频" : "活动音频"}
                  </Typography>
                </Box>

                {detail.mediaType === "video" && videoId && (
                  <VideoContainer>
                    <iframe
                      src={`https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0`}
                      title="YouTube video player"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </VideoContainer>
                )}

                {detail.mediaType === "audio" && audioUrl && (
                  <AudioPlayerContainer>
                    <audio
                      ref={audioRef}
                      src={audioUrl}
                      onTimeUpdate={handleTimeUpdate}
                      onLoadedMetadata={handleLoadedMetadata}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 3,
                      }}
                    >
                      <AudioControlButton onClick={handlePlayPause}>
                        {isPlaying ? (
                          <PauseCircleOutlineIcon />
                        ) : (
                          <PlayCircleOutlineIcon />
                        )}
                      </AudioControlButton>
                      <Box sx={{ flex: 1 }}>
                        <AudioSlider
                          value={currentTime}
                          max={duration}
                          onChange={handleSliderChange}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mt: 0.4,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: "rgba(255, 255, 255, 0.8)",
                              fontSize: "0.85rem",
                              fontWeight: 500,
                              textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                            }}
                          >
                            {formatTime(currentTime)}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "rgba(255, 255, 255, 0.8)",
                              fontSize: "0.85rem",
                              fontWeight: 500,
                              textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                            }}
                          >
                            {formatTime(duration)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </AudioPlayerContainer>
                )}
              </VideoSection>
            )}
          </DetailContent>
        </DetailContainer>
      </DialogContent>
    </GlassDialog>
  );
};

export default VisionSpaceDetail;
